const core = {
  limitsTitle: 'What CAD data you can upload',
  limitsMaxSize: 'Maximum per file: {{size}} MB',
  limitsFormats: 'Supported formats: {{types, list}}',
  limitsBatch: 'Files per thickness batch: {{count}}',
  limitsThicknessEnabled: 'Thickness analysis: available',
  limitsThicknessDisabled: 'Thickness analysis: not enabled for this tenant',
  limitsDisabled: 'CAD features are not enabled for this tenant.',
  title: '3D CAD Viewer',
  prototype: 'Prototype',
  disabledTitle: '3D CAD viewer unavailable',
  disabled: 'This feature is disabled in the current environment. Please contact an administrator.',
  loadModelsFailed: 'Failed to load the model list',
  comparisonFailed: 'Comparison failed: {{message}}',
  deleteFailed: 'Failed to delete the model',
  drbfmLoading: 'Loading DRBFM results...',
  drbfmLoadFailed: 'Failed to fetch DRBFM results: {{message}}',
  drbfmLinked: 'DRBFM session linked: showing {{shown}} / {{total}} failure modes across {{locations}} locations',
  drbfmNoMatchingModel: 'No CAD model linked to the DRBFM session is selected ({{total}} markers belong to another model)',
  uploadedModels: 'Uploaded ({{count}})',
  comparisonTarget: 'Comparison target',
  comparing: 'Comparing...',
  startComparison: 'Start comparison',
  comparisonMode: 'Comparison Mode',
  confidenceLabel: 'Confidence: {{value}}',
  exitComparisonMode: 'Exit comparison mode',
  switchModelConfirm: {
      title: 'Switch model',
      description:
        'The current chat and its markers will be discarded and cannot be restored from this screen. Are you sure you want to switch?',
      confirm: 'Switch',
    },
  metadataTab: 'Metadata',
  aiAnalysisTab: 'AI Analysis',
  chatTab: 'Chat',
  modeSwitch: {
      toThickness: 'Switch to wall thickness check',
      toViewer: 'Return to 3D CAD viewer',
    },
  thickness: {
      errorCodes: {
        conversionFailed: 'CAD conversion failed. Re-export the file and upload it again.',
        analysisFailed: 'Wall-thickness analysis failed. Check the geometry and the analysis conditions.',
        originalMissing: 'The original CAD file is no longer available. Upload it again.',
        internalError: 'An internal error occurred. Contact operations with the model ID.',
      },
      title: 'Wall Thickness Check',
      disabledTitle: 'Wall thickness check unavailable',
      disabled: 'Wall thickness check is not enabled for this tenant.',
      subtitle: 'Face view, violation regions, and verification report',
      scopeNotice: 'Judgement and analysis quality are shown separately. For holes and complex geometry, review the stated limitations and confirm the source CAD manually. All bodies are analyzed in multi-body files.',
      viewerReadOnly: 'Viewer access can inspect results and download reports only.',
      selectModel: 'Select a CAD model from the list.',
      modelProcessing: 'The CAD model is being converted. Thickness analysis can start after completion.',
      modelFailed: 'CAD model conversion failed.',
      loadFailed: 'Failed to load thickness analysis history.',
      rateLimited: 'Too many requests. You can retry in {{seconds}} seconds.',
      retryLoad: 'Retry',
      retryHint: 'The automatic retry also failed. Wait {{seconds}} seconds, then retry.',
      analysisNotFound: 'The analysis ID "{{analysisId}}" from this link does not exist. No substitute result is being shown. Select a valid run from the analysis history.',
      regionNotFound: 'The violation region "{{regionId}}" from this link does not exist in this analysis.',
      selectValidAnalysis: 'Select a valid analysis run',
      startFailed: 'Failed to start thickness analysis.',
      reportFailed: 'Failed to download the verification workbook.',
      analysisConditions: 'Analysis conditions',
      preset: {
        label: 'Condition preset',
        none: 'No preset',
        name: 'Preset name',
        save: 'Save version',
        nameRequired: 'Enter a preset name.',
        loadFailed: 'Failed to load presets.',
        saveFailed: 'Failed to save the preset.',
      },
      lower: 'Lower mm',
      upper: 'Upper mm',
      tolerance: 'Tolerance mm',
      toleranceHelp: 'The measured value is unchanged. The final judgement range is {{lower}}–{{upper}} mm.',
      profile: 'Profile',
      profileValues: {
        standard: 'Standard',
        medium: 'Medium',
        coarse: 'Coarse',
      },
      config: {
        invalidRange: 'The lower limit must not exceed the upper limit.',
        invalidNumber: 'Enter non-negative numbers for the lower limit, upper limit, and tolerance.',
      },
      start: 'Start analysis',
      history: 'Analysis history',
      analysisRun: 'Analysis run',
      latestSuccessful: 'Latest successful',
      noHistory: 'No analysis history.',
      comparison: {
        base: 'Baseline run',
        run: 'Compare',
        failed: 'Failed to compare analysis runs.',
        delta: 'Thin regions {{thin}} / thick regions {{thick}} / changed CAD faces {{faces}} (target minus baseline)',
        assessment: {
          improved: 'Violation regions decreased.',
          regressed: 'Violation regions increased.',
          unchanged: 'Violation region count is unchanged.',
        },
      },
      retry: 'Retry as a new run',
      analysisFailed: 'Analysis failed.',
      analysisRunning: 'Analysis is running and will refresh automatically.',
      recover: 'Recover stopped run',
      recoverFailed: 'Failed to recover the stopped analysis.',
      attempt: 'Attempt {{count}}',
      keepLatestSuccessful: 'The 3D view keeps the latest successful result.',
      result: 'Result',
      smoothView: 'Smooth view',
      flatView: 'Face view',
      legend: {
        title: 'Face-view legend',
        thin: 'Thin',
        within: 'Within range',
        thick: 'Thick',
        tolerance: 'Tolerance applied',
        unmeasured: 'Unmeasured or unsampled',
      },
      thinRegions: 'Thin regions',
      thickRegions: 'Thick regions',
      measuredPoints: 'Measured points',
      pointSupplement: 'Details: {{thin}} thin / {{thick}} thick / {{isolated}} isolated / {{rescued}} tolerance-adjusted points',
      directionReviewRequired: '{{count}} direction-sensitive points require review and are excluded from violation counts',
      directionReviewReason: {
        outward_direction_fallback: 'Outward-ray fallback: {{count}} points',
        same_surface_intersection: 'Intersection with the same CAD surface: {{count}} points',
        opposite_surface_ambiguous: 'Target surface is not oppositely oriented: {{count}} points',
        long_body_span: 'Longitudinal distance close to the full part span: {{count}} points',
      },
      faceSummaries: 'Source CAD face summaries ({{count}})',
      faceSummary: 'Min {{min}} / mean {{mean}} / median {{median}} / P05 {{p05}} / P95 {{p95}} mm\n{{measured}} / {{total}} measured · {{thin}} thin / {{within}} within / {{thick}} thick / {{unmeasured}} unmeasured',
      faceJudgment: {
        mixed: 'Mixed thin and thick',
        thin: 'Has thin points',
        within_range: 'Within range',
        thick: 'Has thick points',
        unmeasured: 'Unmeasured',
      },
      limitations: 'Analysis limitations',
      downloadReport: 'Excel',
      downloadPdf: 'PDF',
      violationRegions: 'Violation regions ({{count}})',
      noViolations: 'No regions fall outside the configured final judgement range.',
      thin: 'Thin',
      thick: 'Thick',
      smoothShadingCaveat: 'Smooth view interpolates colour between mesh vertices, so a face that is uniform in CAD can still appear as a gradient because of neighbouring faces and edges. Use "{{faceView}}" together with a CAD cross-check when judging thickness variation within a face.',
      technicalDetail: '(technical detail)',
      regionCentroidPrecise: 'Region centre: X {{x}} / Y {{y}} / Z {{z}} mm',
      regionExtremeThin: 'Thinnest representative point: X {{x}} / Y {{y}} / Z {{z}} mm',
      regionExtremeThick: 'Thickest representative point: X {{x}} / Y {{y}} / Z {{z}} mm',
      regionSourceFaces: 'CAD faces: {{ids}}',
      reportFileSlug: 'wall-thickness-check',
      regionSummary: 'Min {{min}} / mean {{mean}} / max {{max}} mm · {{count}} points\nCenter X {{x}} / Y {{y}} / Z {{z}} mm\nRepresentative X {{extremeX}} / Y {{extremeY}} / Z {{extremeZ}} mm · {{sourceFaceCount}} CAD faces / {{sourceBodyCount}} bodies ({{faceCount}} mesh faces)',
      regionKind: {
        cluster: 'Proximity cluster',
        isolated: 'Isolated',
      },
      quality: {
        reliable: 'Quality: reliable',
        provisional: 'Quality: provisional',
        limited: 'Quality: limited',
      },
      qualitySource: 'Authoritative result: thickness engine {{engine}}, completed {{completedAt}}. The legacy geometry estimate on the metadata tab is reference-only.',
      review: {
        title: 'Review and approval',
        regionDisposition: 'Selected region disposition',
        comment: 'Review comment',
        save: 'Save review',
        approve: 'Approve result',
        reject: 'Reject',
        loadFailed: 'Failed to load review and approval status.',
        saveFailed: 'Failed to save the review.',
        measurementNonNegative: 'Enter a CAD measurement of 0 or more',
        conflict: 'Another user updated this review. The latest version was reloaded; review it before saving again.',
        status: {
          unreviewed: 'Unreviewed',
          in_review: 'In review',
          approved: 'Approved',
          rejected: 'Rejected',
        },
        disposition: {
          unreviewed: 'Unreviewed',
          requires_change: 'Design change required',
          accepted: 'Accepted',
          false_positive: 'False positive',
          needs_confirmation: 'Needs confirmation',
        },
        stepFlow: '4-step review flow',
        stepsLabel: 'Review steps',
        steps: {
          analysisCheck: 'Analysis check',
          cadVerification: 'CAD verification',
          designAction: 'Design action',
          saveExport: 'Save and export',
        },
        selectRegionHint: 'Select a violation region to zoom to it in 3D and record your review there.',
        analysisConfirmed: 'Analysis quality and target location checked',
        violationRegion: 'Violation region',
        regionPointCount: '1 region · measured points: {{count}}',
        representative: 'Representative values',
        representativeSummary: 'Min {{min}} / mean {{mean}} mm',
        accuracyDisclaimer: '"All 10 representative points within 20% error" is the result of a cross-check between two analysis methods. It does not mean the values match CAD measurements exactly, nor that pass/fail accuracy on the product is 100%.',
        cadVerificationLabel: 'CAD verification method',
        cadVerificationMode: {
          analysis_value: 'Use the analysis value',
          cad_measured: 'Measure in CAD',
          reanalyze: 'Needs reanalysis',
        },
        cadMeasured: 'CAD measurement (mm)',
        analysisDifference: 'Difference from the analysis value',
        measurementMethod: 'Measurement method and location',
        measurementMethodPlaceholder: 'e.g. Lower small-diameter cylindrical face, normal direction confirmed with the CAD measure command',
        rationale: 'Rationale',
        assignee: 'Assignee',
        dueDate: 'Due date',
        evidenceTitle: 'Evidence to be saved',
        evidenceSummary: 'Analysis value {{analysis}} mm / CAD verification {{mode}} / disposition {{disposition}}',
        evidenceSummaryMeasured: 'Analysis value {{analysis}} mm / CAD verification {{mode}} {{measured}} mm / disposition {{disposition}}',
        lastCompleted: 'Last completed: {{name}} / {{at}}',
        back: 'Back',
        next: 'Next',
        unsavedChanges: 'You have unsaved changes.',
      },
      batch: {
        createTitle: 'Create a new batch analysis',
        loadFailed: 'Failed to load batch analyses.',
        startFailed: 'Failed to start the batch analysis.',
        openFailed: 'Failed to open the part analysis result.',
        retryFailed: 'Failed to retry the part.',
        reanalyzeFailed: 'Failed to reanalyze the part with the updated conditions.',
        regionLabel: 'Batch analysis panel',
        scrollHelp: 'The batch analysis panel scrolls vertically. Use "Show all" in each batch to view every part, including completed parts.',
        stepsLabel: 'Batch analysis steps',
        steps: {
          selectFiles: 'Select files',
          conditions: 'Standard conditions',
          submit: 'Submit and analyze',
        },
        selectFiles: 'Select files (up to {{max}})',
        selectedCount: 'Files selected: {{count}}',
        fileSeparator: ', ',
        conditionsTitle: 'Standard conditions',
        showDetails: 'Show details',
        hideDetails: 'Hide details',
        lower: 'Lower',
        upper: 'Upper',
        tolerance: 'Tolerance',
        conditionSummary: 'Range {{lower}}–{{upper}} mm / judgement tolerance {{tolerance}} mm',
        finalRange: 'Final pass range {{lower}}–{{upper}} mm',
        nameLabel: 'Batch analysis name',
        namePlaceholder: 'Batch analysis name (optional)',
        defaultName: 'Batch analysis {{timestamp}}',
        start: 'Start batch analysis',
        submitting: 'Uploading all files…',
        backgroundNotice: 'Once the parts are accepted, processing continues even if you close this screen or shut down your PC.',
        accepted: 'Parts accepted: {{count}}. Analysis continues even if you close the browser.',
        readOnly: 'Creating a batch analysis requires edit permission. Existing results remain viewable.',
        listTitle: 'Batch analyses',
        refresh: 'Refresh',
        empty: 'No batch analyses yet.',
        status: {
          processing: 'Processing',
          needs_review: 'Needs review',
          completed: 'Completed',
          completed_with_failures: 'Partly failed',
          failed: 'Failed',
        },
        itemCounts: 'Needs review {{needsReview}} / completed {{completed}} / failed {{failed}} / {{total}} total',
        showAll: 'Show all',
        showFlaggedOnly: 'Needs review and failed only',
        noFlaggedItems: 'No part needs review and none failed. Use "Show all" to see the completed results.',
        openItem: 'View result',
        retryItem: 'Retry this part only',
        itemProcessing: 'Processing on the server',
        reanalyzeTitle: 'Reanalyze with different conditions',
        reanalyze: 'Reanalyze with these conditions',
        partSearchLabel: 'Search batch analyses',
        partSearchPlaceholder: 'Search batch, part, folder, or tag',
        machineFilter: 'Filter by machine model',
        allMachines: 'All machine models',
        numberFilter: 'Filter by number',
        allNumbers: 'All numbers',
        noPartSearchResults: 'No batch analyses match these filters.',
      },
      status: {
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
      },
      reason: {
        non_watertight: 'The mesh is not watertight, limiting measurement directions.',
        no_measurements: 'No valid measurements were produced.',
        low_hit_rate: 'The measurement hit rate is below 50%.',
        sampled_faces: 'Some faces were sampled due to the profile face limit.',
        medium_profile: 'This is a provisional medium-profile analysis.',
        coarse_profile: 'This is a provisional coarse-profile analysis.',
        multiple_bodies_largest_only: 'Only the largest body in this multi-body file was analyzed.',
        source_face_mapping_unavailable: 'Source CAD face mapping is unavailable, so CAD-face summaries are not shown.',
        untessellated_source_faces: 'Some source CAD faces could not be tessellated, so face summaries are partial.',
      },
    },
};

export default core;
